// Supply chain: every license reports Vendors (upstream — who it buys from) and Customers
// (downstream — who it sells to). Each partner is a DYC business (picked from search) or
// entered manually (it then becomes a DYC sales lead).
//
// MIRRORS server/utils/supplyChain/constants.js — the server is the authority and
// re-validates everything. server/__tests__/supplyChainConstants.contract.test.js fails if
// the two drift. Keep this file free of imports (the contract test evaluates it directly).
//
// ALL user-facing wording for the supply chain lives in SUPPLY_CHAIN_COPY below.

export const DIRECTIONS = ["upstream", "downstream"];

export const PARTNER_TYPES = [
    { value: "cultivator", label: "Cultivator / Farm" },
    { value: "processor", label: "Processor / Producer" },
    { value: "manufacturer", label: "Manufacturer" },
    { value: "wholesaler", label: "Wholesaler" },
    { value: "distributor", label: "Distributor" },
    { value: "retailer", label: "Retailer" },
];
export const partnerTypeLabel = (value) => PARTNER_TYPES.find((t) => t.value === value)?.label || "";

// Linked partners' type is locked to their license profile (1:1).
export const PROFILE_TO_PARTNER_TYPE = {
    cultivator: "cultivator",
    manufacturer: "manufacturer",
    wholesaler: "wholesaler",
    retailer: "retailer",
};

// Minimum entries per list by license profile. null = the list doesn't exist for that
// profile (retailers are the bottom of the chain). Cultivators are the top: vendors optional.
export const LIST_RULES = {
    cultivator: { upstream: 0, downstream: 1 },
    wholesaler: { upstream: 1, downstream: 1 },
    manufacturer: { upstream: 1, downstream: 1 },
    retailer: { upstream: 1, downstream: null },
};
export const DEFAULT_LIST_RULES = { upstream: 1, downstream: 1 };
export const getListRules = (profile) => LIST_RULES[profile] || DEFAULT_LIST_RULES;

export const OPERATION_TYPE_PROFILES = ["cultivator"];
export const OPERATION_TYPES = [
    { value: "indoor", label: "Indoor" },
    { value: "outdoor", label: "Outdoor" },
    { value: "greenhouse", label: "Greenhouse" },
    { value: "mixed", label: "Mixed" },
];

export const PRODUCT_TYPES = ["Flower", "Edibles", "Concentrates", "Drinks", "Vapes", "Other"];

export const MAX_PARTNERS_PER_LIST = 50;
export const PARTNER_NAME_MIN = 2;
export const PARTNER_NAME_MAX = 255;

export const DISPUTE_REASONS = [
    { value: "not_us", label: "That isn't our business" },
    { value: "no_relationship", label: "We don't do business with them" },
    { value: "other", label: "Something else" },
];
export const DISPUTE_NOTE_MAX = 500;

export const US_STATES = [
    "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "DC", "FL", "GA", "HI", "ID", "IL", "IN", "IA", "KS", "KY",
    "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "OH",
    "OK", "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY", "PR",
];

export const SUPPLY_CHAIN_COPY = {
    sectionHint:
        "Tell us who you buy from and who you sell to. This lets DYC trace products from farm to shelf. Search DYC first — if a business isn't on DYC yet, add it by name. Your list is visible only to you and DYC administrators; businesses already on DYC are told that you listed them.",
    lists: {
        upstream: {
            label: "Vendors — who you buy from",
            itemLabel: "vendor",
            addLabel: "+ Add vendor",
            help: "Businesses above you in the supply chain: farms, processors, manufacturers, wholesalers or distributors you buy from.",
        },
        downstream: {
            label: "Customers — who you sell to",
            itemLabel: "customer",
            addLabel: "+ Add customer",
            help: "Businesses below you in the supply chain: retailers, wholesalers, distributors or producers you sell to.",
        },
    },
    requirement: {
        required: "Required",
        optional: "Optional",
    },
    operationType: {
        label: "Operation type",
        placeholder: "Select operation type",
        help: "How you grow. Helps us match COA and traceability records.",
    },
    entry: {
        searchLabel: "Search DYC",
        searchPlaceholder: "Business name (3+ characters)",
        searching: "Searching…",
        noResults: "No DYC businesses match.",
        addManually: "Not on DYC? Add them by name",
        backToSearch: "Search DYC instead",
        onDyc: "On DYC",
        changeBusiness: "Change",
        nameLabel: "Business name",
        typeLabel: "Business type",
        typePlaceholder: "Select type",
        typeLockedHint: "Set by their DYC profile",
        productTypesLabel: "Products you trade with them (optional)",
        moreDetails: "More details (optional)",
        moreDetailsHint: "Anything you know helps us reach them and link them when they join DYC.",
        cityLabel: "City",
        stateLabel: "State",
        usdaLabel: "USDA hemp license #",
        einLabel: "EIN",
        contactNameLabel: "Contact name",
        contactEmailLabel: "Contact email",
        contactPhoneLabel: "Contact phone",
        remove: "Remove",
        disputed: "Under review — this business disputed the listing",
        needsType: "Select a business type",
    },
    errors: {
        name: `Enter ${PARTNER_NAME_MIN}-${PARTNER_NAME_MAX} characters.`,
        type: "Select a business type.",
        duplicate: "Already in this list.",
        email: "Enter a valid email.",
        phone: "Enter a valid phone number.",
        ein: "EIN must be 9 digits.",
        usda: "Use 4-50 letters, numbers or dashes.",
        tooMany: `Maximum ${MAX_PARTNERS_PER_LIST} per list.`,
        operationType: "Select an operation type.",
        required: (itemLabel) => `Add at least one ${itemLabel}.`,
    },
    listedBy: {
        title: "Businesses that list you",
        hint: "Businesses on DYC that reported you as their vendor or customer. If one is wrong, tell us and DYC will review it.",
        empty: "No businesses list you yet.",
        asVendor: "lists you as a vendor",
        asCustomer: "lists you as a customer",
        dispute: "This isn't right",
        disputeTitle: "Dispute this listing",
        disputeReason: "What's wrong?",
        disputeNote: "Anything else DYC should know? (optional)",
        disputeSubmit: "Send to DYC",
        disputeSent: "Thanks — DYC will review this listing.",
    },
};

// ---------------------------------------------------------------------------
// Row helpers. A row in the editor:
// { key, id?, direction, linkedLicenseId?, linkedProfile?, onDyc, partnerName, partnerType,
//   city, state, usdaLicenseNumber, ein, contactName, contactEmail, contactPhone,
//   productTypes[], disputed?, showDetails? }
// ---------------------------------------------------------------------------

let rowCounter = 0;
export const newRowKey = () => `row-${Date.now().toString(36)}-${++rowCounter}`;

export const emptyPartnerRow = (direction) => ({
    key: newRowKey(),
    id: null,
    direction,
    linkedLicenseId: null,
    linkedProfile: null,
    onDyc: false,
    manual: false,
    partnerName: "",
    partnerType: "",
    city: "",
    state: "",
    usdaLicenseNumber: "",
    ein: "",
    contactName: "",
    contactEmail: "",
    contactPhone: "",
    productTypes: [],
});

/** Rows from GET /supply-chain/partners (or an admin view) -> editor rows. */
export const rowsFromPartners = (partners = []) =>
    partners.map((p) => ({
        ...emptyPartnerRow(p.direction),
        id: p.id,
        linkedLicenseId: p.linkedLicenseId || null,
        linkedProfile: p.linkedProfile || null,
        onDyc: Boolean(p.onDyc),
        manual: !p.onDyc,
        partnerName: p.partnerName || "",
        partnerType: p.partnerType || "",
        city: p.city || "",
        state: p.state || "",
        usdaLicenseNumber: p.usdaLicenseNumber || "",
        ein: p.ein || "",
        contactName: p.contactName || "",
        contactEmail: p.contactEmail || "",
        contactPhone: p.contactPhone || "",
        productTypes: p.productTypes || [],
        disputed: Boolean(p.disputed),
    }));

const collapse = (s) => String(s ?? "").trim().replace(/\s+/g, " ");
const isBlankRow = (r) => !r.linkedLicenseId && !collapse(r.partnerName);

// Mirrors the server's company-name key closely enough for duplicate warnings.
const nameKey = (s) =>
    collapse(s)
        .normalize("NFKD")
        .replace(/\p{M}/gu, "")
        .toLowerCase()
        .replace(/&/g, " and ")
        .replace(/[^\p{L}\p{N}]+/gu, " ")
        .trim();

/** Lists shown for a profile (retailers have no customers list). */
export const visibleDirections = (profile) => DIRECTIONS.filter((d) => getListRules(profile)[d] !== null);

/**
 * Client-side validation (the server re-validates). Returns an errors object:
 *   { [`${row.key}.${field}`]: message, upstream?: message, downstream?: message, operationType? }
 */
export function validatePartners(profile, rows, operationType) {
    const errors = {};
    const rules = getListRules(profile);
    for (const direction of visibleDirections(profile)) {
        const list = rows.filter((r) => r.direction === direction && !isBlankRow(r));
        const copy = SUPPLY_CHAIN_COPY.lists[direction];
        if (list.length < (rules[direction] || 0)) errors[direction] = SUPPLY_CHAIN_COPY.errors.required(copy.itemLabel);
        if (list.length > MAX_PARTNERS_PER_LIST) errors[direction] = SUPPLY_CHAIN_COPY.errors.tooMany;
        const seen = new Set();
        for (const r of list) {
            const e = (field, msg) => { errors[`${r.key}.${field}`] = msg; };
            if (!r.linkedLicenseId) {
                const name = collapse(r.partnerName);
                if (name.length < PARTNER_NAME_MIN || name.length > PARTNER_NAME_MAX) e("partnerName", SUPPLY_CHAIN_COPY.errors.name);
                if (!PARTNER_TYPES.some((t) => t.value === r.partnerType)) e("partnerType", SUPPLY_CHAIN_COPY.errors.type);
                if (r.usdaLicenseNumber && !/^[A-Za-z0-9-]{4,50}$/.test(r.usdaLicenseNumber.trim())) e("usdaLicenseNumber", SUPPLY_CHAIN_COPY.errors.usda);
                if (r.ein && r.ein.replace(/\D/g, "").length !== 9) e("ein", SUPPLY_CHAIN_COPY.errors.ein);
            }
            if (r.contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(r.contactEmail.trim())) e("contactEmail", SUPPLY_CHAIN_COPY.errors.email);
            if (r.contactPhone && !/^[0-9+().\-\s]{7,}$/.test(r.contactPhone.trim())) e("contactPhone", SUPPLY_CHAIN_COPY.errors.phone);
            const dup = r.linkedLicenseId ? `lic:${r.linkedLicenseId}` : `name:${nameKey(r.partnerName)}`;
            if (seen.has(dup)) e(r.linkedLicenseId ? "linked" : "partnerName", SUPPLY_CHAIN_COPY.errors.duplicate);
            seen.add(dup);
        }
    }
    if (OPERATION_TYPE_PROFILES.includes(profile) && !OPERATION_TYPES.some((o) => o.value === operationType)) {
        errors.operationType = SUPPLY_CHAIN_COPY.errors.operationType;
    }
    return errors;
}

/**
 * Editor rows -> the `partners` payload. Only lists the profile has; blank rows dropped.
 * Linked rows send only the link + what the reporter adds (identity comes from the license).
 */
export function buildPartnersPayload(profile, rows) {
    const directions = visibleDirections(profile);
    return rows
        .filter((r) => directions.includes(r.direction) && !isBlankRow(r))
        .map((r) => {
            const base = {
                ...(r.id ? { id: r.id } : {}),
                direction: r.direction,
                productTypes: r.productTypes,
                contactName: collapse(r.contactName) || null,
                contactEmail: collapse(r.contactEmail) || null,
                contactPhone: collapse(r.contactPhone) || null,
            };
            if (r.linkedLicenseId) return { ...base, linkedLicenseId: r.linkedLicenseId };
            return {
                ...base,
                partnerName: collapse(r.partnerName),
                partnerType: r.partnerType,
                city: collapse(r.city) || null,
                state: r.state || null,
                usdaLicenseNumber: collapse(r.usdaLicenseNumber) || null,
                ein: collapse(r.ein) || null,
            };
        });
}

/** One empty row per required list, so there is always an input to type into. */
export const initialRows = (profile) =>
    visibleDirections(profile)
        .filter((d) => getListRules(profile)[d] > 0)
        .map((d) => emptyPartnerRow(d));
